import {
  mdBumps,
  Section,
  SectionContent,
  SectionDescription,
  SectionProps,
  SectionTitle,
} from '@wrisk/ui-components'
import { groupBy } from 'lodash-es'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import {
  isMatchingCoverLevelOption,
  PolicyChangeType,
  SelectVoluntaryExcessChange,
} from '../../../../domain'
import { useCoverLevelOption } from '../../../../hooks/coverOption'
import { useErrorHandlingAsyncCallback } from '../../../../hooks/errors'
import {
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { ExcessCombination, PaymentScheduleType } from '../../../../state/configuration'
import { useProduct } from '../../../product/ProductProvider'
import { useProposal } from '../../../product/proposal/ProposalProvider'
import { CoverLevelSelection } from './CoverLevelSelection'
import { ExcessSelection } from './ExcessSelection'
import { PaymentScheduleSelection } from './PaymentScheduleSelection'

const tKey = TKey('components.proposal-configuration-section')

export const ProposalConfigurationSection: FunctionComponent<SectionProps> = (props) => {
  const { t } = useWriskTranslation()

  const {
    loading,
    dispatch,
    commit,
    proposal: { quote },
  } = useProposal()
  const { product } = useProduct()

  const subheader = t(tKey('subheader'), { defaultValue: null })

  const { coverLevelOption } = useCoverLevelOption(quote, product)

  const { coverLevelOptions, coverLevels, coverOptions } = useMemo(() => {
    const coverLevelOptions = groupBy(
      quote.availableCoverLevelOptions,
      (option) => option.coverLevel,
    )

    return {
      coverLevelOptions,
      coverLevels: Object.keys(coverLevelOptions),
      coverOptions: coverLevelOptions[coverLevelOption?.coverLevel ?? ''] ?? [],
    }
  }, [quote])

  const handleCoverLevelChange = useCallback(
    (coverLevel: string) => {
      const newOptions = coverLevelOptions[coverLevel]
      const newMatch = newOptions?.find(
        isMatchingCoverLevelOption({
          paymentScheduleType: coverLevelOption?.paymentScheduleType,
          coverLevel,
        }),
      )

      dispatch({
        type: 'SELECT_COVER_OPTION',
        payload: {
          coverLevel,
          paymentScheduleType:
            newMatch?.paymentScheduleType ??
            newOptions?.[0].paymentScheduleType ??
            coverLevelOption?.paymentScheduleType,
        },
      })
    },
    [quote],
  )

  const handlePaymentScheduleChange = useCallback(
    (paymentScheduleType: PaymentScheduleType) => {
      dispatch({
        type: 'SELECT_COVER_OPTION',
        payload: {
          coverLevel: coverLevelOption?.coverLevel,
          paymentScheduleType,
        },
      })
    },
    [quote],
  )

  const { execute: handleExcessChange } = useErrorHandlingAsyncCallback(
    async (combination: ExcessCombination) => {
      await commit({
        applyRating: true,
        changes: Object.entries(combination).map<SelectVoluntaryExcessChange>(
          ([excessCode, excess]) => ({
            changeType: PolicyChangeType.SELECT_VOLUNTARY_EXCESS,
            excess,
            excessCode,
          }),
        ),
      })
    },
  )

  return quote.availableCoverLevelOptions.length > 1 ? (
    <Section width={1} {...props}>
      <SectionTitle>{t(tKey('header'))}</SectionTitle>
      {subheader && <SectionDescription>{subheader}</SectionDescription>}
      <SectionContent
        display='flex'
        flexDirection='column'
        rowGap={mdBumps}
        p={mdBumps}
        mt={mdBumps}
        variant='raised'
        width={1}
      >
        <CoverLevelSelection
          tKey={tKey}
          coverLevels={coverLevels}
          value={coverLevelOption?.coverLevel}
          onChange={handleCoverLevelChange}
          loading={loading}
        />
        <ExcessSelection
          tKey={tKey}
          quote={quote}
          onChange={handleExcessChange}
          loading={loading}
        />
        <PaymentScheduleSelection
          tKey={tKey}
          coverOptions={coverOptions}
          value={coverLevelOption?.paymentScheduleType}
          onChange={handlePaymentScheduleChange}
          loading={loading}
        />
      </SectionContent>
    </Section>
  ) : null
}
